import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  NgZone,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { TbsButtonModule } from '@ngx-terabase/ui/button';
import { TbsIconModule } from '@ngx-terabase/ui/icon';
import { TbsUploadChangeEvent, TbsUploadModule } from '@ngx-terabase/ui/upload';

import { MissionCreationResult } from '../new-mission/new-mission.component';
import { TbsNotificationModule } from '@ngx-terabase/ui/notification';
import { CommonModule } from '@angular/common';
import {
  FolderItemEvent,
  FolderTaskItemComponent,
} from '../folder-task-item/folder-task-item.component';
import { retry, timer } from 'rxjs';
import { MissionService } from 'src/app/services/mission.service';

export type MissionUploadResult = {
  numberOfMapUploads: number;
  numberOfPanoramaUploads: number;
  numberOfOtherUploads: number;
};

export type FolderTaskStatus =
  | 'created'
  | 'queue'
  | 'uploading'
  | 'paused'
  | 'uploaded';

export type FolderTaskDataType = 'map' | 'pano' | 'other';

export type FolderTask = {
  id: string;
  rootFolderName: string;
  files: TbsUploadChangeEvent;
  status: FolderTaskStatus;
  dataType?: FolderTaskDataType;
};

@Component({
  selector: 'app-upload-mission',
  imports: [
    TbsUploadModule,
    TbsButtonModule,
    TbsIconModule,
    TbsNotificationModule,
    CommonModule,
    FolderTaskItemComponent,
  ],
  templateUrl: './upload-mission.component.html',
  styleUrl: './upload-mission.component.scss',
  standalone: true,
})
export class UploadMissionComponent {
  @Input() public missionCreationResult!: MissionCreationResult;
  @Output() public uploadFinish = new EventEmitter<MissionUploadResult>();
  @ViewChildren(FolderTaskItemComponent)
  taskComponents!: QueryList<FolderTaskItemComponent>;

  public tasks: FolderTask[] = [];
  public showErrorMessage: boolean = false;
  public isUploadAllDisabled: boolean = true;
  public isUploadStarted: boolean = false;

  private missionService = inject(MissionService);

  uploadAll() {
    console.log('Started Uploading at: ', new Date());
    this.isUploadStarted = true;
    for (let task of this.taskComponents) {
      task.setStatus('queue');
    }
    const component = this.findNextTaskComponentInQueue();
    if (component) {
      component.startUploading();
    }
  }

  onUploadEvent({ event }: { event: FolderItemEvent }, task: FolderTask) {
    switch (event) {
      case 'success':
        this.handleSuccessEvent();
        break;
      case 'error':
        this.handleErrorEvent();
        break;
      case 'recovered':
        this.handleRecoverEvent(task);
        break;
      default:
        console.warn(`Unhandled event: ${event}`);
    }
  }

  onFolderSelected(event: TbsUploadChangeEvent): void {
    const task: FolderTask = {
      id: this.generateUUID(),
      rootFolderName: event[0].filePath.split('/')[0],
      files: event,
      status: 'created',
    };
    this.tasks = [...this.tasks, task];
    this.isUploadAllDisabled = !this.getIsUploadAllValid();
  }

  onDataTypeChange(type: FolderTaskDataType, task: FolderTask) {
    task.dataType = type;
    this.isUploadAllDisabled = !this.getIsUploadAllValid();
  }

  onTaskRemove(taskId: string) {
    this.tasks = this.tasks.filter((task) => task.id !== taskId);
    this.isUploadAllDisabled = !this.getIsUploadAllValid();
  }

  private getIsUploadAllValid(): boolean {
    return this.tasks.length > 0 && this.tasks.every((task) => !!task.dataType);
  }

  private handleSuccessEvent(): void {
    const component = this.findNextTaskComponentInQueue();
    if (component) {
      component.startUploading();
    } else {
      console.log('Ended Uploading at: ', new Date());
      this.startProcessing();
    }
  }

  private startProcessing() {
    const {
      numberOfMapUploads,
      numberOfPanoramaUploads,
      numberOfOtherUploads,
    } = this.tasks.reduce(
      (acc, task) => {
        const count = task.files.length;

        switch (task.dataType) {
          case 'map':
            acc.numberOfMapUploads += count;
            break;
          case 'pano':
            acc.numberOfPanoramaUploads += count;
            break;
          case 'other':
            acc.numberOfOtherUploads += count;
            break;
        }

        return acc;
      },
      {
        numberOfMapUploads: 0,
        numberOfPanoramaUploads: 0,
        numberOfOtherUploads: 0,
      }
    );

    this.missionService
      .startMissionProcessing(this.missionCreationResult)
      .pipe(
        retry({
          delay: () => {
            this.showErrorMessage = true;
            return timer(10 * 1000);
          },
        })
      )
      .subscribe({
        next: () => {
          this.showErrorMessage = false;
          this.uploadFinish.emit({
            numberOfMapUploads,
            numberOfPanoramaUploads,
            numberOfOtherUploads,
          });
        },
      });
  }

  private handleErrorEvent(): void {
    this.showErrorMessage = true;
    for (let task of this.taskComponents) {
      if (task.task.status !== 'uploaded') {
        task.setStatus('paused');
      }
    }
  }

  private handleRecoverEvent(task: FolderTask): void {
    this.showErrorMessage = false;

    for (let taskComponent of this.taskComponents) {
      const isUploaded = taskComponent.task.status === 'uploaded';
      const isCurrentTask = taskComponent.task.id === task.id;

      if (!isUploaded) {
        const newStatus: FolderTaskStatus = isCurrentTask
          ? 'uploading'
          : 'queue';
        taskComponent.setStatus(newStatus);
      }
    }
  }

  private findNextTaskComponentInQueue(): FolderTaskItemComponent | undefined {
    return this.taskComponents.find(
      (component) => component.task.status === 'queue'
    );
  }

  private generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
